import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Phone, Mail, Instagram, MapPin, Send, Clock, Heart } from 'lucide-react';
import contactData from '../content/contact.json';

const ContactSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { sectionTitle, description, phone, email, instagram, whatsappNumber, locationTitle, locationDescription, availabilityTitle, availabilityItems, noteTitle, noteText } = contactData;
  
  // CMS format: simple lists are arrays of objects
  const availabilityItemsArray = availabilityItems ? availabilityItems.map(i => i.item || i) : [];

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would normally send the form data to a backend
    // For now, we'll create a WhatsApp link
    const whatsappMessage = `שלום ניצן,
שמי: ${formData.name}
טלפון: ${formData.phone}
${formData.email ? `אימייל: ${formData.email}` : ''}
${formData.service ? `מעוניין/ת ב: ${formData.service}` : ''}

הודעה:
${formData.message}`;
    
    const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappLink, '_blank');
  };

  const contactMethods = [
    {
      icon: <Phone size={28} />,
      title: 'טלפון',
      value: phone,
      link: `tel:${phone}`,
      gradient: 'from-nitzan-mint to-nitzan-teal'
    },
    {
      icon: <Instagram size={28} />,
      title: 'אינסטגרם',
      value: instagram,
      link: `https://instagram.com/${instagram.replace('@', '')}`,
      gradient: 'from-nitzan-teal to-nitzan-sage'
    },
    {
      icon: <Mail size={28} />,
      title: 'דוא"ל',
      value: email,
      link: `mailto:${email}`,
      gradient: 'from-nitzan-sage to-nitzan-terracotta'
    }
  ];

  return (
    <section id="contact" className="section-padding bg-white">
      <div className="container-width">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.div
            className="inline-flex items-center gap-2 bg-gradient-to-r from-nitzan-mint/20 to-nitzan-teal/20 px-6 py-3 rounded-full mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            <Heart className="text-nitzan-teal" size={20} />
            <span className="text-nitzan-teal font-medium">יצירת קשר</span>
          </motion.div>

          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="gradient-text">{sectionTitle}</span>
          </h2>
          
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            {description}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="bg-gradient-to-br from-nitzan-light-bg to-white rounded-3xl p-8 shadow-xl border border-nitzan-sage/20"
          >
            <h3 className="text-2xl font-bold text-nitzan-teal mb-6">שלחי לי הודעה</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-nitzan-teal font-semibold mb-2">שם מלא *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border-2 border-nitzan-sage/30 focus:border-nitzan-teal focus:outline-none transition-colors"
                  placeholder="איך קוראים לך?"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-nitzan-teal font-semibold mb-2">טלפון *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border-2 border-nitzan-sage/30 focus:border-nitzan-teal focus:outline-none transition-colors"
                  placeholder="מספר הטלפון שלך"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-nitzan-teal font-semibold mb-2">דוא"ל (אופציונלי)</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border-2 border-nitzan-sage/30 focus:border-nitzan-teal focus:outline-none transition-colors"
                  placeholder="כתובת המייל שלך"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-nitzan-teal font-semibold mb-2">במה את/ה מעוניין/ת?</label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border-2 border-nitzan-sage/30 focus:border-nitzan-teal focus:outline-none transition-colors"
                >
                  <option value="">בחרי...</option>
                  <option value="קבוצה">קבוצה</option>
                  <option value="סדנה">סדנה</option>
                  <option value="ליווי אישי">ליווי אישי</option>
                  <option value="מפה נומרולוגית">מפה נומרולוגית</option>
                  <option value="תטא הילינג">תטא הילינג</option>
                  <option value="הזמנה אישית">הזמנה אישית</option>
                  <option value="אחר">אחר</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-nitzan-teal font-semibold mb-2">הודעה *</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 rounded-xl border-2 border-nitzan-sage/30 focus:border-nitzan-teal focus:outline-none transition-colors resize-none"
                  placeholder="ספרי לי קצת על עצמך ומה את מחפשת..."
                ></textarea>
              </div>

              <motion.button
                type="submit"
                className="w-full bg-gradient-to-r from-nitzan-teal to-nitzan-mint text-white px-6 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                <Send size={20} />
                <span>שלחי הודעה</span>
              </motion.button>

              <p className="text-sm text-gray-600 text-center">
                * לחיצה על הכפתור תפתח את ווטסאפ עם ההודעה מוכנה
              </p>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="space-y-6"
          >
            {/* Contact Methods */}
            <div className="space-y-4">
              {contactMethods.map((method, index) => (
                <motion.a
                  key={index}
                  href={method.link}
                  target={method.link.includes('http') ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  whileHover={{ scale: 1.02, y: -2 }}
                  className={`block bg-gradient-to-r ${method.gradient} text-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      {method.icon}
                    </div>
                    <div>
                      <div className="text-sm font-medium opacity-90">{method.title}</div>
                      <div className="text-xl font-bold">{method.value}</div>
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>

            {/* Location/Hours */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.9 }}
              className="bg-white rounded-2xl p-6 shadow-lg border border-nitzan-sage/20"
            >
              <div className="flex items-start gap-4 mb-4">
                <MapPin className="text-nitzan-teal flex-shrink-0" size={28} />
                <div>
                  <h4 className="text-lg font-bold text-nitzan-teal mb-2">{locationTitle}</h4>
                  <p className="text-gray-700">
                    {locationDescription}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 1.0 }}
              className="bg-gradient-to-br from-nitzan-sage/10 to-nitzan-mint/10 rounded-2xl p-6 border border-nitzan-sage/20"
            >
              <div className="flex items-start gap-4">
                <Clock className="text-nitzan-teal flex-shrink-0" size={28} />
                <div>
                  <h4 className="text-lg font-bold text-nitzan-teal mb-2">{availabilityTitle}</h4>
                  {availabilityItemsArray.map((item, index) => (
                    <p key={index} className={index < availabilityItemsArray.length - 1 ? "text-gray-700 mb-2" : "text-gray-600 text-sm mt-3"}>
                      {item}
                    </p>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Quick Note */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 1.1 }}
              className="bg-gradient-to-r from-nitzan-teal to-nitzan-mint text-white rounded-2xl p-6 shadow-lg"
            >
              <Heart className="mb-3" size={32} />
              <h4 className="text-xl font-bold mb-2">{noteTitle}</h4>
              <p className="leading-relaxed">
                {noteText}
              </p>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom Note */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="text-center bg-gradient-to-r from-nitzan-sage/20 via-nitzan-mint/20 to-nitzan-teal/20 rounded-3xl p-8"
        >
          <p className="text-xl text-gray-700 mb-2">
            <strong className="text-nitzan-teal">זוכרת:</strong> אין שאלות מיותרות, אין "עדיין לא מוכנה"
          </p>
          <p className="text-lg text-gray-600">
            כל מסע מתחיל בצעד קטן. הצעד הזה יכול להיות פשוט הודעה קטנה 💚
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactSection;




